// 링크별 통계 (IMP-7) — 목록에서 바로 열어 보는 상세
import { useEffect, useState } from "react";
import { daysAgo, isoDate, statsApi, type LinkStats } from "../api/stats.ts";
import { ApiError } from "@taplink/web-shared";
import { DailyClicksChart } from "./DailyClicksChart.tsx";

const PRESETS = [7, 30, 90] as const;

export function LinkStatsDialog({
  shortKey,
  shortUrl,
  onClose,
}: {
  shortKey: string;
  shortUrl: string;
  onClose: () => void;
}) {
  const [days, setDays] = useState<number>(7);
  const [stats, setStats] = useState<LinkStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    statsApi
      .link(shortKey, daysAgo(days - 1), isoDate(new Date()))
      .then((result) => {
        if (!cancelled) setStats(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "통계를 불러오지 못했습니다.");
      });
    return () => {
      cancelled = true;
    };
  }, [shortKey, days]);

  // ⚠️ 나가는 길이 **바닥의 '닫기' 버튼 하나뿐**이었다. '표로 보기'를 켜면 레이어가
  //    화면보다 길어져 그 버튼에 닿을 수 없었다(실측 2026-08-18 — 갇혔다).
  //    높이 제한은 CSS 가 맡고, 여기서는 나가는 길을 둘 더 만든다:
  //    Escape 키와 배경 클릭. 수정 레이어(LinkEditDialog)와 같은 방식이다.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`${shortUrl} 통계`}
      onClick={onClose}
    >
      {/* 레이어 안쪽 클릭은 배경으로 새어 나가면 안 된다 — 새면 조작할 때마다 닫힌다 */}
      <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <h2>링크 통계</h2>
        <p className="hint"><code>{shortUrl}</code></p>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <div className="filter-row" role="group" aria-label="조회 기간">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={days === preset ? "chip chip--on" : "chip"}
              aria-pressed={days === preset}
              onClick={() => setDays(preset)}
            >
              최근 {preset}일
            </button>
          ))}
        </div>

        {stats && (
          <>
            {stats.range.truncated && (
              <div className="alert alert-info" role="status">
                현재 요금제는 통계를 <strong>{stats.range.retentionDays}일</strong>까지 보관합니다.
              </div>
            )}
            <div className="kpi-grid">
              <div className="kpi">
                <span className="kpi-label">기간 클릭</span>
                <strong className="kpi-value">{stats.rangeClicks.toLocaleString("ko-KR")}</strong>
              </div>
              <div className="kpi">
                <span className="kpi-label">누적 클릭</span>
                <strong className="kpi-value">{stats.totalClicks.toLocaleString("ko-KR")}</strong>
              </div>
            </div>
            <DailyClicksChart points={stats.daily} title="일별 클릭 수" />
          </>
        )}

        {!stats && !error && <p role="status">불러오는 중…</p>}

        {/* QR 레이어의 닫기와 **같은 물건**이다 — 같은 모습이어야 한다(styles.css 의 .modal-close) */}
        <button type="button" className="btn-primary modal-close" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}
